import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid, useMediaQuery, useTheme as useMuiTheme } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { InfoBlock } from "../../components/containers/InfoBlock"
import { TopFeaturesGraph } from "../../components/graphs/Bar/TopFeaturesGraph"
import { BasePieChart } from "../../components/graphs/PieChart"
import { SankeyChart, type SankyChartData } from "../../components/graphs/SankeyChart"
import { KonanEmptyState } from "../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../services/KonanAPI"
import { LabelReasonsInsight } from "../../types/generated/api/LabelReasonsInsight"
import type { WorkflowSimulationReport } from "../../types/generated/api/WorkflowSimulationReport"
import type { PieChartContainerProps, ReportV1Props } from "../Simulations/Interfaces"
import { formatTopFeatures, getDecisionFunnelData } from "./Helpers"

/**
 * Pie chart container to hold the data and select state to enable selecting which label  to display
 * @param {boolean} isLoading
 * @param {number} index
 * @param data
 * @returns {React.ReactElement}
 */
function PieChartContainer(props: Readonly<PieChartContainerProps>): React.ReactElement {
  const { isLoading, data, index } = props

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationReport.reportV0",
  })

  const [selectedReason, setSelectedReason] = useState<string>("")

  // sets the selected reason using the index passed when rendering after loading
  // ensuring that on initial load no label is selected twice
  useEffect(() => {
    !isLoading && setSelectedReason(data[index]?.label.name as string)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <BasePieChart
      title={t("reasons", { reason: selectedReason })}
      graphHeight={160}
      isLoading={isLoading}
      data={
        data.find((item) => item.label.name === selectedReason)?.reasons ?? [
          {
            name: "",
            value: 0,
            percent: 0,
          },
        ]
      }
      label={
        data.find((item) => item.label.name === selectedReason)?.label ?? {
          name: "",
          value: 0,
          percent: 0,
        }
      }
      selectProps={{
        options: data.map((item) => item.label.name),
        value: selectedReason,
        setValue: (value) => {
          setSelectedReason(value)
        },
      }}
      headerMode="select"
    />
  )
}

/**
 * The ReportV0 component renders a simulation report based on the provided
 * props. It fetches insight data and processes it to display various graphs
 * and information blocks about the simulation results.
 *
 * Features:
 * - Displays request count, dataset information, and date range
 * - Renders pie charts for decision reasons breakdown
 * - Displays a decision funnel using a Sankey diagram
 * - Shows top features related to the simulation
 * - Handles loading states and displays an empty state for pending/running simulations
 *
 * @param {Readonly<ReportV1Props>} props - The properties for the component.
 * @param {boolean} props.isReportResLoading - Indicates if the report resources are loading.
 * @param {SimulationJobRetrieve} props.simulation - The simulation job details.
 * @param {Report} props.reportData - The report data object containing simulation details.
 * @param {ReactElement | undefined} props.predictionDateRange - The date range for predictions.
 * @returns {React.ReactElement} A React element representing the simulation report.
 */

export function ReportV0(props: Readonly<ReportV1Props>): React.ReactElement {
  const { isReportResLoading, simulation, reportData, predictionDateRange } = props

  const { id: projectId } = useParams<{ id: string }>()

  const muiTheme = useMuiTheme()

  const mdQuery = useMediaQuery(muiTheme.breakpoints.down("md"))
  const smQuery = useMediaQuery(muiTheme.breakpoints.down("sm"))

  const { t } = useTranslation("simulations", {
    keyPrefix: "simulationReport.reportV0",
  })

  const { data: insightReportData, isFetching: isInsightsReportLoading } = useQuery<
    AxiosResponse<LabelReasonsInsight>,
    AxiosError,
    { report: WorkflowSimulationReport; totalRequests: number; totalFailed: number } | undefined
  >(
    ["insight", simulation?.uuid, projectId, simulation?.report, reportData.insights?.[0]?.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: simulation?.report,
        insight_uuid: reportData.insights[0]?.uuid as string,
      }),
    {
      select({ data }) {
        if (data.version === "V0") {
          return {
            report: data.data.report,
            totalRequests: data.data.total_requests,
            totalFailed: data.data.total_failed,
          }
        }
        return undefined
      },
      enabled: !!projectId && !!simulation?.uuid && !!simulation?.report && !!reportData.insights[0]?.uuid,
    },
  )

  const DecisionFunnelData = useMemo(
    () =>
      getDecisionFunnelData({
        node_percentages: insightReportData?.report.node_percentages,
        totalRequests: insightReportData?.totalRequests,
      }),
    [insightReportData?.report.node_percentages, insightReportData?.totalRequests],
  )

  // preprocessing simulation report data to map it in data that can be rendered in the pie charts
  const reasons = useMemo(() => {
    const reasonList: {
      reasons: { name: string; value: number; percent: number }[]
      label: { name: string; percent: number; value: number }
    }[] = []

    insightReportData?.report.labels.forEach((item) => {
      const totalItems = item.total

      const subReasons: { name: string; value: number; percent: number }[] = []
      const subLabel = {
        name: item.name,
        percent: Math.round((item.total / insightReportData.totalRequests) * 100),
        value: item.total,
      }

      item.breakdown.forEach((breakdownItem) => {
        subReasons.push({
          name: `${breakdownItem.name ?? breakdownItem.return_label?.name ?? ""} (${
            breakdownItem.node_type.charAt(0).toUpperCase() + breakdownItem.node_type.slice(1)
          })`,
          value: breakdownItem.count,
          percent: Math.round((breakdownItem.count / totalItems) * 100),
        })
      })

      reasonList.push({ reasons: subReasons, label: subLabel })
    })

    return { reasonList }
  }, [insightReportData])

  const isLoading = isInsightsReportLoading || isReportResLoading

  if (!["Running", "Pending"].includes(simulation.status ?? "")) {
    return (
      // Simulation Report
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} lg={4}>
            <InfoBlock
              title={t("requestCount")}
              text={insightReportData?.totalRequests ?? ""}
              isLoading={isLoading}
              type="default"
            />
          </Grid>

          <Grid item xs={12} lg={4}>
            <InfoBlock
              title={t("dataset")}
              text={simulation.predictions_datafile?.name ?? t("livePredictions")}
              isLoading={isLoading}
              type="default"
            />
          </Grid>
          {predictionDateRange && (
            <Grid item xs={12} lg={4}>
              <InfoBlock title={t("dateRange")} text={predictionDateRange} isLoading={isLoading} type="default" />
            </Grid>
          )}
        </Grid>
        <Fragment>
          {reasons.reasonList.slice(0, smQuery ? 1 : mdQuery ? 2 : 3).map((_, index) => {
            return (
              <Grid item sm={12} md={6} lg={4} key={index}>
                <PieChartContainer isLoading={isLoading} data={reasons ? reasons.reasonList : []} index={index} />
              </Grid>
            )
          })}

          <Grid item xs={12}>
            <SankeyChart
              title={t("sankeyChart.title")}
              graphHeight={340}
              data={DecisionFunnelData as SankyChartData}
              isLoading={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TopFeaturesGraph
              title={t("topFeaturesGraph.title")}
              graphHeight={340}
              showAxis={false}
              visible
              isLoading={isLoading}
              data={formatTopFeatures(insightReportData?.report.labels || [])}
            />
          </Grid>
        </Fragment>
      </Grid>
    )
  } else {
    return (
      // Empty Simulation list empty state
      <Grid item height={"100%"} xs={12} alignContent={"center"}>
        <KonanEmptyState
          title={t("emptyState.loading.title", { status: simulation.status })}
          subTitle={t("emptyState.loading.subtitle")}
        />
      </Grid>
    )
  }
}
